/**
 * Global display priority for enriched attributes. Attributes listed here render
 * first, in this order, across every attribute surface (product detail page,
 * quick view, content-enrichment sample review, feed-audit compare); everything
 * else keeps its existing relative order after them.
 *
 * Keys are the enrichment wire field names, but matching is format-insensitive
 * (see `normalizeAttributeKey`) so the one list works against every id shape the
 * surfaces carry — snake_case wire names (`product_highlight`), camelCase sample
 * keys (`productHighlight`), plural curation names (`product_highlights`), and
 * pretty labels (`Product Highlights`).
 */
export const ATTRIBUTE_PRIORITY: readonly string[] = [
  'title',
  'structured_title',
  'description',
  'structured_description',
  'product_highlight',
  'product_detail',
  'product_type',
  'color',
  'material',
  'pattern',
];

/** Folds a field key or label to a comparison token: lowercase, alphanumerics
 *  only, trailing plural `s` dropped — so `product_highlight`, `Product
 *  Highlights`, `productHighlight` and `product_highlights` all collapse to the
 *  same token. Matches the whole token (not a prefix), so `materialOrigin` never
 *  masquerades as `material`. */
function normalizeAttributeKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .replace(/s$/, '');
}

const PRIORITY_RANK = new Map(
  ATTRIBUTE_PRIORITY.map((key, index) => [normalizeAttributeKey(key), index] as const),
);

/**
 * Priority index for an attribute — lower sorts higher; non-priority attributes
 * all rank last (equal). Pass the identifiers in order of preference (wire
 * `field` first, `label` as a fallback); the first that matches wins.
 */
export function attributePriorityRank(...keys: (string | null | undefined)[]): number {
  for (const key of keys) {
    if (!key) continue;
    const rank = PRIORITY_RANK.get(normalizeAttributeKey(key));
    if (rank !== undefined) return rank;
  }
  return ATTRIBUTE_PRIORITY.length;
}

/**
 * Stable sort placing priority attributes first (in {@link ATTRIBUTE_PRIORITY}
 * order) and leaving every other item in its original relative order. `keyOf`
 * returns the attribute's field key (or label) to match on.
 */
export function sortByAttributePriority<T>(items: T[], keyOf: (item: T) => string): T[] {
  return [...items].sort(
    (a, b) => attributePriorityRank(keyOf(a)) - attributePriorityRank(keyOf(b)),
  );
}
